import React from "react";
import { Box, Tooltip } from "@mui/material";
import { groupByLayer } from "../../services/scenes/captionPayload";

/**
 * CaptionLayerSummary — the scene list's Layers cell (OPUS-1391).
 *
 * One chip per layer showing how many captions sit on it. This replaced a list
 * of caption NAMES, which never fit: a 20-caption scene rendered as two
 * arbitrary pills and a "+18", and pill width varied with name length so the
 * column could not be scanned down.
 *
 * The names are not lost — hovering a chip lists them. A real tooltip rather
 * than a native `title`, because title has a ~1s delay, cannot be styled, and
 * cannot show the disabled state that the old caption pills conveyed with a
 * strikethrough.
 *
 * All three layers always render, in stacking order, so the counts line up down
 * the column; empty ones dim rather than disappear.
 *
 * Its own component rather than an inline cell renderer so this is testable
 * without standing up a DataGrid.
 */

const captionLabel = (caption) => caption?.caption_name || caption?.id || "";

const LayerTooltip = ({ label, color, rows }) => (
  <Box sx={{ py: 0.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: rows.length ? 0.75 : 0 }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)" }}>
        {label}
      </Box>
    </Box>
    {rows.length === 0 ? (
      <Box sx={{ fontSize: 12, color: "var(--text-dim)", fontStyle: "italic" }}>No captions on this layer.</Box>
    ) : (
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.25 }}>
        {rows.map(({ caption }, i) => {
          const name = captionLabel(caption);
          if (!name) return null;
          return (
            <Box
              component="li"
              key={caption.id || i}
              sx={{ fontSize: 12, fontFamily: "var(--font-mono)", color: caption.disabled ? "var(--text-dim)" : "var(--text)", textDecoration: caption.disabled ? "line-through" : "none", whiteSpace: "nowrap" }}
            >
              {name}
              {caption.disabled && (
                <Box component="span" sx={{ ml: 0.75, fontSize: 10.5, color: "var(--amber)", textDecoration: "none", fontFamily: "var(--font-sans)" }}>
                  disabled
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    )}
  </Box>
);

const CaptionLayerSummary = ({ captions }) => {
  const list = captions || [];
  if (list.length === 0) return <Box component="span" sx={{ color: "var(--text-dim)" }}>—</Box>;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
      {groupByLayer(list).map((group) => {
        const empty = group.rows.length === 0;
        return (
          <Tooltip
            key={group.value}
            arrow
            placement="top"
            enterDelay={120}
            title={<LayerTooltip label={group.label} color={group.color} rows={group.rows} />}
          >
            <Box
              component="span"
              aria-label={`${group.label}: ${group.rows.length} caption${group.rows.length === 1 ? "" : "s"}`}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.7, height: 22, boxSizing: "border-box", fontSize: 11.5, color: empty ? "var(--text-dim)" : "var(--text-muted)", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "999px", px: 1.1, whiteSpace: "nowrap", flexShrink: 0, opacity: empty ? 0.45 : 1, cursor: "default" }}
            >
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: group.color, flexShrink: 0 }} />
              {group.label}
              <Box component="span" sx={{ fontWeight: 700, color: empty ? "var(--text-dim)" : "var(--text)" }}>
                {group.rows.length}
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default CaptionLayerSummary;
